import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Sparkles } from 'lucide-react';
import {
  getCleaningTasks,
  deleteCleaningTask,
  updateCleaningTaskStatus,
  updateCleaningTaskPhotos,
  addCleaningWorkNote,
  deleteCleaningWorkNote,
  getWorkers,
  assignCleaningTask,
} from '@/lib/api';
import { toast } from 'sonner';
import CleaningDialog from '@/components/dialogs/CleaningDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent } from '@/components/ui/dialog';

import { CleaningTaskCard } from './cleaning/CleaningTaskCard';
import { PhotosDialog } from './cleaning/PhotosDialog';
import { WorkNotesDialog } from './cleaning/WorkNotesDialog';
import { MyTasksToggle, useMyTasksFilter, isMineForTask } from '@/components/MyTasksToggle';
import { useAuth } from '@/contexts/AuthContext';

/**
 * CleaningView — Kanban of cleaning tasks (Pending / In Progress /
 * Completed). Refactored 2026-Feb to hand off card + dialog rendering
 * to dedicated sub-components under /components/cleaning/. This file
 * is now purely an orchestrator: data fetching, callbacks, and layout.
 */
export const CleaningView = ({ selectedProperty, properties, onRefresh }) => {
  const { user } = useAuth();
  const [onlyMine, setOnlyMine] = useMyTasksFilter();
  const [tasks, setTasks] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const [viewPhotoUrl, setViewPhotoUrl] = useState(null);
  const [newWorkNote, setNewWorkNote] = useState('');
  const [newWorkHours, setNewWorkHours] = useState('');

  // ---- data fetching ----
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getCleaningTasks(selectedProperty?.id);
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching cleaning tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty]);

  const fetchWorkers = useCallback(async () => {
    try {
      const response = await getWorkers(true);
      setWorkers(response.data);
    } catch (error) {
      console.error('Error fetching workers:', error);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchWorkers();
  }, [fetchTasks, fetchWorkers]);

  // ---- mutations ----
  const handleDelete = async () => {
    if (!taskToDelete) return;
    try {
      await deleteCleaningTask(taskToDelete.id);
      toast.success('Cleaning task deleted');
      fetchTasks();
      onRefresh?.();
    } catch (_) {
      toast.error('Failed to delete task');
    } finally {
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
    }
  };

  const handleStatusChange = async (taskId, status) => {
    try {
      await updateCleaningTaskStatus(taskId, status);
      toast.success('Status updated');
      fetchTasks();
    } catch (_) {
      toast.error('Failed to update status');
    }
  };

  const handleAssignWorker = async (taskId, workerId) => {
    try {
      await assignCleaningTask(taskId, workerId);
      toast.success('Worker assigned');
      fetchTasks();
    } catch (_) {
      toast.error('Failed to assign worker');
    }
  };

  const handleAddPhoto = async (url) => {
    if (!selectedTask || !url?.trim()) return;
    try {
      const updatedPhotos = [...(selectedTask.photos || []), url.trim()];
      await updateCleaningTaskPhotos(selectedTask.id, updatedPhotos);
      toast.success('Photo added');
      fetchTasks();
      setSelectedTask((prev) => ({ ...prev, photos: updatedPhotos }));
    } catch (_) {
      toast.error('Failed to add photo');
    }
  };

  const handleRemovePhoto = async (photoIndex) => {
    if (!selectedTask) return;
    try {
      const updatedPhotos = (selectedTask.photos || []).filter((_, i) => i !== photoIndex);
      await updateCleaningTaskPhotos(selectedTask.id, updatedPhotos);
      toast.success('Photo removed');
      fetchTasks();
      setSelectedTask((prev) => ({ ...prev, photos: updatedPhotos }));
    } catch (_) {
      toast.error('Failed to remove photo');
    }
  };

  const handleAddWorkNote = async () => {
    if (!selectedTask || !newWorkNote.trim()) return;
    try {
      const hours = parseFloat(newWorkHours) || 0;
      const result = await addCleaningWorkNote(selectedTask.id, newWorkNote.trim(), hours);
      toast.success('Work note added');
      setNewWorkNote('');
      setNewWorkHours('');
      fetchTasks();
      const updatedTask = tasks.find((t) => t.id === selectedTask.id);
      if (updatedTask) {
        setSelectedTask({
          ...updatedTask,
          work_notes: [
            ...(updatedTask.work_notes || []),
            { note: newWorkNote, hours, timestamp: new Date().toISOString() },
          ],
          hours_worked: result.data.total_hours,
        });
      }
    } catch (_) {
      toast.error('Failed to add work note');
    }
  };

  const handleDeleteWorkNote = async (noteId) => {
    if (!selectedTask) return;
    try {
      await deleteCleaningWorkNote(selectedTask.id, noteId);
      toast.success('Work note deleted');
      fetchTasks();
      setSelectedTask((prev) => ({
        ...prev,
        work_notes: (prev.work_notes || []).filter((n) => n.id !== noteId),
      }));
    } catch (_) {
      toast.error('Failed to delete work note');
    }
  };

  const getPropertyName = useCallback(
    (propertyId) => properties.find((p) => p.id === propertyId)?.name || 'Unknown Property',
    [properties]
  );

  // ---- Kanban column split ----
  const visibleTasks = useMemo(() => {
    if (!onlyMine) return tasks;
    return tasks.filter((t) => isMineForTask(t, user?.id));
  }, [tasks, onlyMine, user?.id]);

  const mineCount = useMemo(
    () => tasks.filter((t) => isMineForTask(t, user?.id)).length,
    [tasks, user?.id]
  );

  const { pendingTasks, inProgressTasks, completedTasks } = useMemo(() => ({
    pendingTasks: visibleTasks.filter((t) => t.status === 'pending'),
    inProgressTasks: visibleTasks.filter((t) => t.status === 'in_progress'),
    completedTasks: visibleTasks.filter((t) => t.status === 'completed'),
  }), [tasks]);

  // ---- shared card renderer ----
  const renderCard = (task) => (
    <CleaningTaskCard
      key={task.id}
      task={task}
      workers={workers}
      propertyName={getPropertyName(task.property_id)}
      onStatusChange={handleStatusChange}
      onAssignWorker={handleAssignWorker}
      onOpenPhotos={(t) => {
        setSelectedTask(t);
        setPhotoDialogOpen(true);
      }}
      onOpenNotes={(t) => {
        setSelectedTask(t);
        setNotesDialogOpen(true);
      }}
      onDelete={(t) => {
        setTaskToDelete(t);
        setDeleteDialogOpen(true);
      }}
      onPreviewPhoto={setViewPhotoUrl}
    />
  );

  return (
    <div className="space-y-6" data-testid="cleaning-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">
            Cleaning Tasks
          </h1>
          <p className="text-slate-500 mt-1">
            {selectedProperty ? selectedProperty.name : 'All Properties'} - Manage cleaning schedules
          </p>
        </div>
        <div className="flex items-center gap-2">
          <MyTasksToggle
            enabled={onlyMine}
            onChange={setOnlyMine}
            count={mineCount}
            testId="cleaning-my-tasks-toggle"
          />
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-cleaning-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Task
          </Button>
        </div>
      </div>

      {/* Tasks */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : tasks.length === 0 ? (
        <Card className="bg-white border border-slate-100">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Sparkles className="h-16 w-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No cleaning tasks</h3>
            <p className="text-slate-500 mb-4">
              Cleaning tasks are auto-created after bookings checkout
            </p>
            <Button onClick={() => setDialogOpen(true)} data-testid="add-first-cleaning-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Task
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[
            { label: 'Pending', dot: 'bg-amber-500', items: pendingTasks, empty: 'No pending tasks' },
            { label: 'In Progress', dot: 'bg-blue-500', items: inProgressTasks, empty: 'No tasks in progress' },
            { label: 'Completed', dot: 'bg-emerald-500', items: completedTasks, empty: 'No completed tasks' },
          ].map((col) => (
            <div key={col.label}>
              <div className="flex items-center gap-2 mb-4">
                <div className={`w-3 h-3 rounded-full ${col.dot}`}></div>
                <h2 className="font-semibold text-slate-900">
                  {col.label} ({col.items.length})
                </h2>
              </div>
              <div className="space-y-3">
                {col.items.map(renderCard)}
                {col.items.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">{col.empty}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialogs */}
      <CleaningDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          fetchTasks();
          onRefresh?.();
        }}
        properties={properties}
        selectedProperty={selectedProperty}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Cleaning Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this cleaning task?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-cleaning-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-cleaning-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PhotosDialog
        open={photoDialogOpen}
        onOpenChange={setPhotoDialogOpen}
        task={selectedTask}
        onAddUrl={handleAddPhoto}
        onRemove={handleRemovePhoto}
        onPreview={setViewPhotoUrl}
      />

      <WorkNotesDialog
        open={notesDialogOpen}
        onOpenChange={setNotesDialogOpen}
        task={selectedTask}
        newWorkNote={newWorkNote}
        setNewWorkNote={setNewWorkNote}
        newWorkHours={newWorkHours}
        setNewWorkHours={setNewWorkHours}
        onAddNote={handleAddWorkNote}
        onDeleteNote={handleDeleteWorkNote}
      />

      {/* Full Photo View */}
      <Dialog open={!!viewPhotoUrl} onOpenChange={() => setViewPhotoUrl(null)}>
        <DialogContent className="sm:max-w-[800px] p-2">
          {viewPhotoUrl && (
            <img src={viewPhotoUrl} alt="Full view" className="w-full h-auto rounded-lg" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CleaningView;
